import React, { useState, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import api from '../../services/api';
import Modal from './Modal';

const LABEL = 'block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1';
const PERFIL_ROTULO = { admin: 'Administrador', gerente: 'Gestor', marketing: 'Marketing', corretor: 'Corretor' };

function formatWhatsapp(v) {
  const d = String(v || '').replace(/\D/g, '').slice(0, 13);
  if (d.length <= 2) return d;
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
}

// reduz a foto no navegador (quadrada, 400px, JPEG) antes de enviar
function reduzirImagem(file, lado = 400) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const min = Math.min(img.width, img.height);
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = Math.min(lado, min);
      canvas.getContext('2d').drawImage(img, (img.width - min) / 2, (img.height - min) / 2, min, min, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob(b => (b ? resolve(b) : reject(new Error('falha'))), 'image/jpeg', 0.88);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('imagem inválida')); };
    img.src = url;
  });
}

export function iniciaisDe(nome) {
  return (nome || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('');
}

function getUser() {
  try { return JSON.parse(localStorage.getItem('user') || 'null') || {}; } catch { return {}; }
}

export default function PerfilModal({ isOpen, onClose, onSalvo }) {
  const [form, setForm] = useState({ nome: '', whatsapp: '', instagram: '', funcao: '', data_nascimento: '' });
  const [senha, setSenha] = useState({ atual: '', nova: '', confirmar: '' });
  const [msgPerfil, setMsgPerfil] = useState(null);
  const [msgSenha, setMsgSenha] = useState(null);
  const [user, setUser] = useState(getUser());
  const fileRef = useRef(null);
  const [msgFoto, setMsgFoto] = useState(null);

  // carrega o perfil atualizado do servidor ao abrir
  useEffect(() => {
    if (!isOpen) return;
    setUser(getUser()); setMsgFoto(null); setMsgPerfil(null); setMsgSenha(null); setSenha({ atual: '', nova: '', confirmar: '' });
    api.get('/auth/me').then(r => {
      const u = r.data.data;
      setUser(p => ({ ...p, ...u }));
      setForm({ nome: u.nome || '', whatsapp: formatWhatsapp(u.whatsapp), instagram: u.instagram ? `@${u.instagram}` : '', funcao: u.funcao || '', data_nascimento: u.data_nascimento || '' });
    }).catch(() => {});
  }, [isOpen]);

  const set = (f) => (e) => setForm(p => ({ ...p, [f]: f === 'whatsapp' ? formatWhatsapp(e.target.value) : e.target.value }));

  const perfilMut = useMutation({
    mutationFn: () => api.put('/auth/perfil', form),
    onSuccess: (r) => {
      localStorage.setItem('user', JSON.stringify({ ...getUser(), ...r.data.data }));
      setMsgPerfil({ ok: true, texto: 'Perfil salvo' });
      onSalvo?.(r.data.data);
    },
    onError: (e) => setMsgPerfil({ ok: false, texto: e.response?.data?.error || 'Erro ao salvar perfil' }),
  });

  const aplicarUsuario = (u) => {
    localStorage.setItem('user', JSON.stringify({ ...getUser(), ...u }));
    setUser(p => ({ ...p, ...u }));
    onSalvo?.(u);
  };

  const fotoMut = useMutation({
    mutationFn: async (file) => {
      const blob = await reduzirImagem(file);
      const fd = new FormData();
      fd.append('foto', blob, 'foto.jpg');
      return api.put('/auth/foto', fd);
    },
    onSuccess: (r) => { aplicarUsuario(r.data.data); setMsgFoto({ ok: true, texto: 'Foto atualizada' }); },
    onError: (e) => setMsgFoto({ ok: false, texto: e.response?.data?.error || 'Não foi possível enviar a foto' }),
  });
  const removerFotoMut = useMutation({
    mutationFn: () => api.delete('/auth/foto'),
    onSuccess: (r) => { aplicarUsuario(r.data.data); setMsgFoto({ ok: true, texto: 'Foto removida' }); },
  });

  const senhaMut = useMutation({
    mutationFn: () => api.put('/auth/senha', { senhaAtual: senha.atual, novaSenha: senha.nova }),
    onSuccess: () => { setSenha({ atual: '', nova: '', confirmar: '' }); setMsgSenha({ ok: true, texto: 'Senha alterada' }); },
    onError: (e) => setMsgSenha({ ok: false, texto: e.response?.data?.error || 'Erro ao alterar senha' }),
  });

  const trocarSenha = (e) => {
    e.preventDefault();
    if (senha.nova !== senha.confirmar) return setMsgSenha({ ok: false, texto: 'A confirmação não confere com a nova senha' });
    senhaMut.mutate();
  };

  const Msg = ({ m }) => m && <p className={`text-sm ${m.ok ? 'text-green-600' : 'text-red-600'}`}>{m.texto}</p>;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Meu perfil" size="md">
      <div className="space-y-6">
        <form onSubmit={e => { e.preventDefault(); setMsgPerfil(null); perfilMut.mutate(); }} className="space-y-3">
          <div className="flex items-center gap-4">
            {user.avatar_url
              ? <img src={user.avatar_url} alt="" className="w-20 h-20 rounded-full object-cover border border-gray-200" />
              : <span className="w-20 h-20 rounded-full bg-primary text-white text-2xl font-bold flex items-center justify-center">{iniciaisDe(user.nome)}</span>}
            <div className="space-y-1">
              <p className="text-xs text-gray-500">{user.email} · <span className="font-medium">{PERFIL_ROTULO[user.perfil] || user.perfil}</span></p>
              <div className="flex gap-2">
                <button type="button" disabled={fotoMut.isPending} onClick={() => fileRef.current?.click()} className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                  {fotoMut.isPending ? 'Enviando...' : user.avatar_url ? 'Trocar foto' : 'Adicionar foto'}
                </button>
                {user.avatar_url && (
                  <button type="button" onClick={() => removerFotoMut.mutate()} className="px-3 py-1.5 text-xs text-gray-500 hover:text-red-600">Remover</button>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) { setMsgFoto(null); fotoMut.mutate(f); } }} />
              {msgFoto && <p className={`text-xs ${msgFoto.ok ? 'text-green-600' : 'text-red-600'}`}>{msgFoto.texto}</p>}
            </div>
          </div>
          <div>
            <label className={LABEL}>Nome *</label>
            <input className="input-field w-full" value={form.nome} onChange={set('nome')} required />
          </div>
          <div>
            <label className={LABEL}>Função / cargo</label>
            <input className="input-field w-full" value={form.funcao} onChange={set('funcao')} placeholder="Ex: Gerente comercial" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>WhatsApp</label>
              <input className="input-field w-full" value={form.whatsapp} onChange={set('whatsapp')} inputMode="tel" placeholder="(44) 99999-9999" />
            </div>
            <div>
              <label className={LABEL}>Instagram</label>
              <input className="input-field w-full" value={form.instagram} onChange={set('instagram')} placeholder="@usuario" />
            </div>
          </div>
          <div>
            <label className={LABEL}>Data de nascimento</label>
            <input type="date" className="input-field w-full" value={form.data_nascimento} onChange={set('data_nascimento')} />
          </div>
          <Msg m={msgPerfil} />
          <div className="flex justify-end">
            <button type="submit" disabled={perfilMut.isPending} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-secondary disabled:opacity-50">
              {perfilMut.isPending ? 'Salvando...' : 'Salvar perfil'}
            </button>
          </div>
        </form>

        <form onSubmit={trocarSenha} className="space-y-3 border-t pt-5">
          <h3 className="text-sm font-semibold text-gray-700">Alterar senha</h3>
          <div>
            <label className={LABEL}>Senha atual</label>
            <input type="password" autoComplete="current-password" className="input-field w-full" value={senha.atual} onChange={e => setSenha(p => ({ ...p, atual: e.target.value }))} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>Nova senha</label>
              <input type="password" autoComplete="new-password" minLength={8} className="input-field w-full" value={senha.nova} onChange={e => setSenha(p => ({ ...p, nova: e.target.value }))} required />
            </div>
            <div>
              <label className={LABEL}>Confirmar</label>
              <input type="password" autoComplete="new-password" minLength={8} className="input-field w-full" value={senha.confirmar} onChange={e => setSenha(p => ({ ...p, confirmar: e.target.value }))} required />
            </div>
          </div>
          <p className="text-xs text-gray-400">Mínimo de 8 caracteres.</p>
          <Msg m={msgSenha} />
          <div className="flex justify-end">
            <button type="submit" disabled={senhaMut.isPending} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
              {senhaMut.isPending ? 'Alterando...' : 'Alterar senha'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
